import { Injectable } from '@angular/core';
import { Observable, Subject, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { BrowserStorageService } from './browser-storage.service'; // Import BrowserStorageService
import { tap, catchError } from 'rxjs/operators'; // Import tap operator
import { EncryptionService } from './encryption.service';

interface SendEmailCodeBody {
  identifier: string;
}
interface recoverPasswordBody {
  //identifier: string;
  password: string;
  code: string;
  password_confirm: string;
}

interface LoginCredentials {
  emailOrPhone: string;
  password: string;
}
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  apiUrl = environment.apiUrl;
  private baseUrl = `${this.apiUrl}/api/users/auth/login/`;

  private _clearLoginForm = new Subject<void>();
  clearLoginForm$ = this._clearLoginForm.asObservable();

  private _pendingLoginCredentials: LoginCredentials | null = null; // Temporary storage

  constructor(private http: HttpClient, private browserStorage: BrowserStorageService, private encryption: EncryptionService) { }

  setPendingLoginCredentials(credentials: LoginCredentials): void {
    this._pendingLoginCredentials = credentials;
  }

  getPendingLoginCredentials(): LoginCredentials | null {
    return this._pendingLoginCredentials;
  }

  clearPendingLoginCredentials(): void {
    this._pendingLoginCredentials = null;
  }

  /**
   * Llama al endpoint de login del backend.
   * @param emailOrPhone El correo electrónico o número de teléfono del usuario.
   * @param password La contraseña del usuario.
   * @returns Un Observable con la respuesta del backend.
   */
  login(emailOrPhone: string, password: string): Observable<any> {
    const body = {
      email: emailOrPhone,
      password: password
    };

    // POST a http://<apiUrl>/authentication/user/login/
    return this.http.post(`${this.baseUrl}`, body).pipe(
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Canjea el ticket de handoff entre dominios: devuelve exactamente lo que
   * habría devuelto el login en ESTE dominio (`mfa_token`, o el par de tokens).
   *
   * El ticket es opaco, de UN SOLO USO y vive 60 s; lo emite el back cuando la
   * marca del usuario vive en otro dominio, y lo valida contra el `Origin` de
   * este canje. El front solo lo transporta: no lo guarda ni lo reintenta.
   *
   * Va con `Skip-Auth-Interceptor` (mismo patrón que `verifyIdentify`) porque
   * todavía no hay sesión que adjuntar y porque un 400/403 acá lo muestra el
   * login — no el modal de error global del interceptor (R7: sin ruido).
   */
  canjearHandoff(ticket: string): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Skip-Auth-Interceptor': 'true',
    });
    return this.http.post(`${this.apiUrl}/api/users/auth/handoff/`, { ticket }, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      }),
    );
  }

  sendEmailCode(body: SendEmailCodeBody): Observable<any> {
    let encryptedAccess: any;
    if(this.browserStorage.getSessionStorageItem('mfa_token_identify')){
      encryptedAccess = this.browserStorage.getSessionStorageItem('access_identify');
      //console.log("identify token")
    }
    else{
      encryptedAccess = this.browserStorage.getSessionStorageItem('access');
      //console.log("token normaaaaal")
    }
    const access = this.encryption.decrypt(String(encryptedAccess));
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${access}`
    });
    return this.http.post(`${this.apiUrl}/api/users/auth/send-verification-code/`, body,  { headers: headers});
  }

  recoverPassword(body: recoverPasswordBody): Observable<any> {
    const encryptedAccess = this.browserStorage.getSessionStorageItem('access_identify');
    const access = this.encryption.decrypt(String(encryptedAccess));
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${access}`
    });
    return this.http.post(`${this.apiUrl}/api/users/auth/reset-password/`, body, { headers: headers});
  }

  forceLogout(body: any): Observable<any> {
    // Assuming the force-logout endpoint is different from reset-password
    return this.http.post(`${this.apiUrl}/api/users/auth/force-logout/`, body).pipe(
      tap(() => {
        // Clear mfa_token from local storage after successful force logout
        this.browserStorage.removeSessionStorageItem('mfa_token');
      })
    );
  }

  verifyIdentify(body: any): Observable<any> {
    const headers = new HttpHeaders({
      'Skip-Auth-Interceptor': 'true'
    });
    return this.http.post(`${this.apiUrl}/api/users/auth/verify-identity/`, body, { headers: headers});
  }

  triggerClearLoginForm(): void {
    this._clearLoginForm.next();
  }
}
